import React from "react";

import { useResourceAutoLogAPIInterface } from "@dealer-center/ddc-pages-resource-auto-log-fetch";
import { Alert, Button, Table } from "@dealer-center/core-components";
import { LoadingIndicator } from "@dealer-center/core-custom-components";
import { useNavigate, useOutletContext } from "react-router-dom";
import styled from "styled-components";

import type { AccountEntity } from "@dealer-center/ddc-api-account-interface";
import type { Resources } from "@dealer-center/ddc-pages-resource-auto-log-interface";
import type { TypeSafeTableColumns } from "@dealer-center/core-components";

const StyledPagesResourceAutoLogUi = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
  width: 100%;
`;

const StyledTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
`;

const StyledSubTitle = styled.span`
  font-weight: 400;
  font-size: 14px;
`;

const StyledResultsArea = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 1rem;
`;

const StyledFooter = styled.div`
  display: flex;
  align-items: end;
  justify-content: end;
  margin-bottom: 2rem;
`;

const standardizeDateTime = (dateTimeString: string): string => {
  if (!dateTimeString) {
    return dateTimeString;
  }
  // timezone regex, i.e. +01:00
  const regex = /[+-]\d\d:\d\d/g;
  // format dateTimeString with UTC timezone
  const utcDateTimeString = `${dateTimeString}${regex.test(dateTimeString) ? "" : " +00:00"}`;
  return new Date(utcDateTimeString).toLocaleString();
};

export const PagesResourceAutoLogUiDisplay = () => {
  const { accountEntity } = useOutletContext<{
    accountEntity: AccountEntity;
  }>();
  const {
    data: resources,
    error,
    isLoading
  } = useResourceAutoLogAPIInterface({
    dealerId: accountEntity?.id
  });

  const navigate = useNavigate();

  const handleBackButtonClick = () => {
    navigate(`/dealer/${accountEntity.id}/dashboard/resources`);
  };
  const columns: TypeSafeTableColumns<Resources> = [
    {
      dataIndex: "Service Account",
      title: "Service Account"
    },
    {
      dataIndex: "Resource Name",
      title: "Resource Name"
    },
    {
      dataIndex: "Action",
      title: "Action"
    },
    {
      dataIndex: "Action Date",
      title: "Action Date",
      // convert UTC to locale time
      render: (text: string) => standardizeDateTime(text)
    },
    {
      dataIndex: "Action By",
      title: "Action By"
    },
    {
      dataIndex: "User ID",
      title: "User Id"
    },
    {
      dataIndex: "Acting As User ID",
      title: "Acting As User ID"
    }
  ];

  return (
    <StyledPagesResourceAutoLogUi>
      <StyledHeader>
        <StyledTitle>Resource Audit Trail</StyledTitle>
        <StyledSubTitle>
          Audit data is only available for resources that were configured after
          July 2024
        </StyledSubTitle>
      </StyledHeader>
      <StyledResultsArea>
        {error && (
          <StyledAlert role="alert" sx={{ marginBottom: "16px" }} type="error">
            {error?.message || "Error"}
          </StyledAlert>
        )}
        {isLoading && (
          <LoadingIndicator
            actionName="Data Loading"
            data-testid="resource-audit-trail-loading-indicator"
          />
        )}
        {resources && (
          <Table.Table
            virtual
            borderType="body"
            columns={columns}
            data={resources}
            displayFilter={true}
            enablePagination={false}
            scrollX={1}
            scrollY={600}
            sortableColumns={true}
          />
        )}
        {!isLoading && (
          <StyledFooter>
            <Button
              analyticsConfiguration={{
                eventName: "Audit History - Back to Dashboard"
              }}
              buttonStyle="secondary"
              onClick={handleBackButtonClick}
            >
              Back
            </Button>
          </StyledFooter>
        )}
      </StyledResultsArea>
    </StyledPagesResourceAutoLogUi>
  );
};
